// src/utils/skillNormalization.js

/**
 * Builds a JS RegExp that matches all naming variants of a skill.
 * "ReactJS" → matches: "React", "React.js", "React JS", "ReactJS", "React-JS"
 * "Node"    → matches: "Node", "Node.js", "NodeJS"
 * "Java"    → matches: "Java" ONLY — does NOT match "JavaScript" (anchors)
 */
export const buildSkillRegex = (skill) => {
    const trimmed = skill.trim();

    // Compound skills with "&" or "and" — exact case-insensitive only
    if (/[&]|\band\b/i.test(trimmed)) {
        const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return new RegExp(`^${escaped}$`, "i");
    }

    const base = trimmed.toLowerCase().replace(/[\s.\-_\/,]+/g, "");

    // Remove trailing "js": "reactjs" → "react", "nodejs" → "node"
    // but "js" alone stays "js", "java" stays "java" (doesn't end in "js")
    const withoutJs =
        base.endsWith("js") && base.length > 2 ? base.slice(0, -2) : base;

    const escapedChars = withoutJs
        .split("")
        .map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

    // Optional separators between chars + optional "js" suffix
    const pattern =
        escapedChars.join("[ .\\-_]*") + "[ .\\-_]*(js)?";

    // ^ and $ anchors: "java" pattern won't match "JavaScript"
    return new RegExp(`^${pattern}$`, "i");
};

/**
 * Normalizes a skill to a grouping key (used for deduplication).
 */
export const normalizeSkillKey = (skill) => {
    const trimmed = skill.trim();

    if (/[&]|\band\b/i.test(trimmed)) {
        return trimmed.toLowerCase().replace(/\s+/g, " ");
    }

    const cleaned = trimmed.toLowerCase().replace(/[\s.\-_\/,]+/g, "");

    if (cleaned.endsWith("js") && cleaned.length > 2) {
        return cleaned.slice(0, -2);
    }

    return cleaned;
};

/**
 * Scores a skill name to pick the best "canonical" display version.
 * Prefers: starts uppercase, no trailing spaces/punctuation, no spaces, shorter.
 */
const getCanonicalScore = (skill) => {
    const trimmed = skill.trim();
    let score = 0;
    if (/^[A-Z]/.test(trimmed)) score += 10;
    if (!/[,;\s]$/.test(trimmed)) score += 5;
    if (!trimmed.includes(" ")) score += 2;
    score -= trimmed.replace(/[\s.\-_\/,]+/g, "").length * 0.1;
    return score;
};

/**
 * Builds a deduplicated, normalized list of unique technology chip labels
 * from all employees' skills arrays.
 *
 * Examples:
 *   "React", "React JS", "ReactJS", "React.js" → one chip: "React"
 *   "Node", "Node.js", "NodeJS"                → one chip: "Node"
 *   "React Native"                             → separate chip ✓
 *   "Vue JS & Node JS"                         → separate chip ✓
 *   "Java"                                     → separate chip ✓ (not grouped with JavaScript)
 *   "JavaScript", "Javascript", "Java script"  → one chip: "JavaScript"
 */
export const buildUniqueTechnologies = (employees) => {
    const groups = {};

    employees.forEach((emp) => {
        (emp.skills || []).forEach((rawSkill) => {
            const skill = rawSkill.trim();
            if (!skill) return;

            const key = normalizeSkillKey(skill);
            const score = getCanonicalScore(skill);

            if (!groups[key]) {
                groups[key] = { canonical: skill, score };
            } else if (score > groups[key].score) {
                groups[key].canonical = skill;
                groups[key].score = score;
            }
        });
    });

    return Object.values(groups)
        .map(({ canonical }) => canonical)
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
};